import { Logo } from "@/assets/logo";
import { PublicRoutes } from "@/constants/routes";
import { Button, Heading, Text } from "@/design-system";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router";

/**
 * Página não encontrada.
 *
 * O "404" é o marco da tela, e por isso sai em `text-ds-display` — o maior
 * degrau da escala, reservado justamente para abertura de tela. Ele perdeu o
 * gradiente: fundo degradê em texto exige `text-transparent`, então onde o
 * recorte falha o número some por inteiro, e a marca aqui é sóbria, não
 * decorada. Como é repetição do título que vem abaixo, continua `aria-hidden`.
 *
 * Esta tela vive fora do layout do dashboard, então o `<main>` é dela mesma.
 */
export const NotFound = () => {
  const navigate = useNavigate();

  return (
    <main className="bg-ds-paper flex min-h-screen flex-col items-center justify-center px-4 py-16 text-center sm:px-6">
      <Helmet>
        <title>Página não encontrada (404) | Fidexa</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Link
        to={PublicRoutes.HOME}
        aria-label="Voltar para a página inicial da Fidexa"
        className="rounded-ds-md focus-visible:ring-ds-focus focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        <Logo size={140} />
      </Link>

      <div className="mt-12 flex max-w-prose flex-col items-center gap-3">
        <span
          aria-hidden="true"
          className="font-ds-display text-ds-display text-ds-brand-700 leading-none font-bold"
        >
          404
        </span>

        <Heading level={1} className="mt-3">
          Esta página não existe
        </Heading>

        <Text size="body" tone="muted">
          O endereço que você tentou acessar pode ter sido removido, renomeado
          ou nunca ter existido. Vamos te levar de volta ao caminho certo.
        </Text>

        {/* A ação principal primeiro, e as duas ocupando a largura toda em
            360px: botão de meia tela ao lado do outro encolhe o alvo de toque
            justamente na tela em que o dedo é o ponteiro. */}
        <div className="mt-7 flex w-full max-w-sm flex-col gap-3 sm:w-auto sm:flex-row">
          <Button asChild navigates="internal" fullWidth className="sm:w-auto">
            <Link to={PublicRoutes.HOME}>Ir para o início</Link>
          </Button>

          <Button
            variant="secondary"
            navigates="back"
            fullWidth
            className="sm:w-auto"
            onClick={() => navigate(-1)}
          >
            Voltar
          </Button>
        </div>
      </div>
    </main>
  );
};
